/**
 * Every configurable value in the app, read once from import.meta.env.
 *
 * Names match scripts/seed-env.js, which is what writes these into SSM and
 * materializes .env; see .env.example. Nothing else in the app should touch
 * import.meta.env directly.
 */

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

const rawApiUrl = import.meta.env.VITE_API_URL ?? '';
const apiUrl = trimTrailingSlash(rawApiUrl || 'http://localhost:3000');

/** Which Slate this panel is looking at, inferred from the API it talks to. */
export type Stage = 'local' | 'development' | 'production';

function stageOf(url: string): Stage {
  if (url.includes('localhost') || url.includes('127.0.0.1')) return 'local';
  if (url.includes('dev-api.')) return 'development';
  return 'production';
}

const stage = stageOf(apiUrl);

/** The customer app that belongs to this API, for "open in Slate" links. */
function defaultAppOrigin(current: Stage): string {
  if (current === 'production') return 'https://app.slatevideo.com';
  if (current === 'development') return 'https://dev-app.slatevideo.com';
  return 'http://localhost:5173';
}

export const config = {
  /** Origin of slate-api. Calls are made to `${apiUrl}/v1/admin/...`. */
  apiUrl,
  stage,
  appOrigin: trimTrailingSlash(import.meta.env.VITE_APP_ORIGIN || defaultAppOrigin(stage)),

  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_CONFIG_APIKEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_CONFIG_AUTHDOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_CONFIG_PROJECTID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_CONFIG_STORAGEBUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_CONFIG_MESSAGINGSENDERID ?? '',
    appId: import.meta.env.VITE_FIREBASE_CONFIG_APPID ?? '',
  },
} as const;

/** Is Firebase configured well enough to attempt auth? */
export const firebaseConfigured = Boolean(config.firebase.apiKey && config.firebase.projectId);

export function shareUrl(shareId: string): string {
  return `${config.appOrigin}/v/${shareId}`;
}

/**
 * Stripe's dashboard, in the mode this stage's keys are in. Development and
 * local run on test keys; only production is live.
 */
export function stripeCustomerUrl(customerId: string): string {
  const base = stage === 'production' ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test';
  return `${base}/customers/${customerId}`;
}

export function stripeSubscriptionUrl(subscriptionId: string): string {
  const base = stage === 'production' ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test';
  return `${base}/subscriptions/${subscriptionId}`;
}
